import { ScrollingModule } from '@angular/cdk/scrolling';
import { DatePipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import localeEnZA from '@angular/common/locales/en-ZA';
import { LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideNativeDateAdapter } from '@angular/material/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { NgSelectModule } from '@ng-select/ng-select';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { ToastrModule } from 'ngx-toastr';
import { AppRoutingModule } from './app-routing.module';
import { MaterialModule } from './app.material';
import { EnterpriseWalletBalanceComponent } from './enterprise/enterprise-wallet-balance/enterprise-wallet-balance.component';

import { InlineStylesCSPModule } from './inline-styles-csp/inline-styles-csp.module';

import { SyncfusionSharedModule } from 'src/app/shared/syncfusion-shared.module';
import { TableComponentModule } from 'src/app/table-component/table-component.module';
import { AdminEnterpriseWalletBalancePageComponent } from './admin/admin-enterprise-wallet-balance-page/admin-enterprise-wallet-balance-page.component';
import { AgentTermsComponent } from './admin/admin-settings/agent-terms/agent-terms.component';
import { FeesComponent } from './admin/admin-settings/fees/fees.component';
import { LevelsAndLimitsComponent } from './admin/admin-settings/levels-and-limits/levels-and-limits.component';
import { MerchantTermsComponent } from './admin/admin-settings/merchant-terms/merchant-terms.component';
import { PersonalTermsComponent } from './admin/admin-settings/personal-terms/personal-terms.component';
import { PrivacyPolicyComponent } from './admin/admin-settings/privacy-policy/privacy-policy.component';
import { TermsAndConditionsComponent } from './admin/admin-settings/terms-and-conditions/terms-and-conditions.component';
import { ActiveApplicationsComponent } from './admin/agent-applications/active-applications/active-applications.component';
import { AgentFilesComponent } from './admin/agent-applications/agent-files/agent-files.component';
import { AgentApplicationsComponent } from './admin/agent-applications/applications/agent-applications.component';
import { DeniedApplicationsComponent } from './admin/agent-applications/denied-applications/denied-applications.component';
import { BannersComponent } from './admin/banner/index/banners.component';
import { NewBannerComponent } from './admin/banner/new-banner/new-banner.component';
import { DashboardComponent } from './admin/dashboard/dashboard.component';
import { EftCashInDataComponent } from './admin/eft-cash-in/eft-cash-in-data/eft-cash-in-data.component';
import { EftCashInComponent } from './admin/eft-cash-in/eft-cash-in/eft-cash-in.component';
import { GrvComponent } from './admin/grv/grv.component';
import { AgentComponent } from './admin/manage-users/add-user/agent/agent.component';
import { BankComponent } from './admin/manage-users/add-user/bank/bank.component';
import { EnterpriseUserComponent } from './admin/manage-users/add-user/enterprise-user/enterprise-user.component';
import { StaffUserComponent } from './admin/manage-users/add-user/staff-user/staff-user.component';
import { ChangeMobileComponent } from './admin/manage-users/change-mobile/change-mobile.component';
import { ManageComponent } from './admin/manage-users/manage/manage.component';
import { OtpsComponent } from './admin/manage-users/otps/otps.component';
import { SearchComponent } from './admin/manage-users/search/search.component';
import { VerifyOtpComponent } from './admin/manage-users/verify-otp/verify-otp.component';
import { VerifyUsersComponent } from './admin/manage-users/verify-users/verify-users.component';
import { TransactionsComponent } from './admin/manage-users/view/transactions/transactions.component';
import { UserAgentProfileComponent } from './admin/manage-users/view/user-agent-profile/user-agent-profile.component';
import { UserMerchantProfileComponent } from './admin/manage-users/view/user-merchant-profile/user-merchant-profile.component';
import { UserPersonalProfileComponent } from './admin/manage-users/view/user-personal-profile/user-personal-profile.component';
import { UserProfileFilesComponent } from './admin/manage-users/view/user-profile-files/user-profile-files.component';
import { UserProfileSettingsComponent } from './admin/manage-users/view/user-profile-settings/user-profile-settings.component';
import { WalletSummaryComponent } from './admin/manage-users/view/wallet-summary/wallet-summary.component';
import { RmsSimulatorComponent } from './admin/rms-simulator/rms-simulator.component';
import { SmartSafeComponent } from './admin/smart-safe/smart-safe/smart-safe.component';
import { CitAddCompanyComponent } from './admin/smart-safes/cash-in-transit/cit-add-company/cit-add-company.component';
import { CitCompaniesComponent } from './admin/smart-safes/cash-in-transit/cit-companies/cit-companies.component';
import { SafeEventsComponent } from './admin/smart-safes/safe-events/safe-events.component';
import { SafeSummaryComponent } from './admin/smart-safes/safe-summary/index/safe-summary.component';
import { SafeSummaryDetailsComponent } from './admin/smart-safes/safe-summary/safe-summary-details/safe-summary-details.component';
import { SafeTransactionDetailsComponent } from './admin/smart-safes/safe-summary/safe-transaction-details/safe-transaction-details.component';
import { SafeUserDetailsComponent } from './admin/smart-safes/safe-summary/safe-user-details/safe-user-details.component';
import { SafesConfigurationComponent } from './admin/smart-safes/safes-configuration/index/safes-configuration.component';
import { SmartSafeModalComponent } from './admin/smart-safes/safes-configuration/smart-safe-modal/smart-safe-modal.component';
import { SmartSafesComponent } from './admin/smart-safes/smart-safes/smart-safes.component';
import { AllTransactionsComponent } from './admin/transactions/all-transactions/all-transactions.component';
import { TransferToBankLogComponent } from './admin/transfer-to-bank/transfer-to-bank-log/transfer-to-bank-log.component';
import { TransferToBankManualExportComponent } from './admin/transfer-to-bank/transfer-to-bank-manual-export/transfer-to-bank-manual-export.component';
import { TransferToBankPendingComponent } from './admin/transfer-to-bank/transfer-to-bank-pending/transfer-to-bank-pending.component';
import { TransferToBankProcessedComponent } from './admin/transfer-to-bank/transfer-to-bank-processed/transfer-to-bank-processed.component';
import { AppComponent } from './app.component';
import { CompanyComponent } from './company/company.component';
import { EnterpriseLinksComponent } from './enterprise/common/enterprise-links/enterprise-links.component';
import { EnterpriseNavigationComponent } from './enterprise/common/enterprise-navigation/enterprise-navigation.component';
import { EnterpriseCashbackSettingsComponent } from './enterprise/enterprise-cashback-settings/enterprise-cashback-settings.component';
import { EnterpriseCashbacksRedeemedComponent } from './enterprise/enterprise-cashbacks-redeemed/enterprise-cashbacks-redeemed.component';
import { EnterpriseDashboardComponent } from './enterprise/enterprise-dashboard/enterprise-dashboard.component';
import { EnterpriseEmployeesComponent } from './enterprise/enterprise-employees/enterprise-employees.component';
import { EnterpriseHeaderComponent } from './enterprise/enterprise-header/enterprise-header.component';
import { EnterpriseProfileComponent } from './enterprise/enterprise-profile/enterprise-profile.component';
import { EnterpriseSettingsComponent } from './enterprise/enterprise-settings/enterprise-settings.component';
import { EnterpriseTransactionsComponent } from './enterprise/enterprise-transactions/enterprise-transactions.component';
import { EnterpriseTransferToBankComponent } from './enterprise/enterprise-transfer-to-bank/enterprise-transfer-to-bank.component';
import { EnterpriseWalletSummaryComponent } from './enterprise/enterprise-wallet-summary/enterprise-wallet-summary.component';
import { AdminLayoutComponent } from './layouts/admin-layout/admin-layout.component';
import { EnterpriseLayoutComponent } from './layouts/enterprise-layout/enterprise-layout.component';
import { CreatePasswordComponent } from './login-pages/create-password/create-password.component';
import { ForgotPasswordComponent } from './login-pages/forgot-password/forgot-password.component';
import { LoginComponent } from './login-pages/login/login.component';
import { TwoFactorAuthComponent } from './login-pages/two-factor-auth/two-factor-auth.component';
import { HttpRequestInterceptor } from './services/http-request-interceptor';
import { SharedModule } from './shared/shared.module';

registerLocaleData(localeEnZA);

@NgModule({
    declarations: [
        AppComponent,
        AdminLayoutComponent,
        LoginComponent,
        DashboardComponent,
        StaffUserComponent,
        AgentComponent,
        SearchComponent,
        ChangeMobileComponent,
        VerifyOtpComponent,
        ManageComponent,
        ForgotPasswordComponent,
        PrivacyPolicyComponent,
        AgentApplicationsComponent,
        ActiveApplicationsComponent,
        DeniedApplicationsComponent,
        LevelsAndLimitsComponent,
        FeesComponent,
        TermsAndConditionsComponent,
        AgentFilesComponent,
        PersonalTermsComponent,
        MerchantTermsComponent,
        AgentTermsComponent,
        VerifyUsersComponent,
        TransactionsComponent,
        EnterpriseUserComponent,
        BankComponent,
        CreatePasswordComponent,
        EnterpriseLayoutComponent,
        EnterpriseNavigationComponent,
        EnterpriseProfileComponent,
        EnterpriseDashboardComponent,
        EnterpriseEmployeesComponent,
        EnterpriseTransactionsComponent,
        EnterpriseLinksComponent,
        EnterpriseCashbackSettingsComponent,
        EnterpriseSettingsComponent,
        EnterpriseWalletBalanceComponent,
        TwoFactorAuthComponent,
        SmartSafeComponent,
        AllTransactionsComponent,
        WalletSummaryComponent,
        EnterpriseWalletSummaryComponent,
        EnterpriseTransferToBankComponent,
        TransferToBankProcessedComponent,
        TransferToBankPendingComponent,
        TransferToBankManualExportComponent,
        TransferToBankLogComponent,
        EftCashInComponent,
        EftCashInDataComponent,
        GrvComponent,
        RmsSimulatorComponent,
        CompanyComponent,
        BannersComponent,
        NewBannerComponent,
        SmartSafesComponent,
        SafeSummaryComponent,
        SafeSummaryDetailsComponent,
        SafesConfigurationComponent,
        SafeEventsComponent,
        SmartSafeModalComponent,
        SafeTransactionDetailsComponent,
        SafeUserDetailsComponent,
        EnterpriseHeaderComponent,
        CitCompaniesComponent,
        CitAddCompanyComponent,
        EnterpriseCashbacksRedeemedComponent,
        OtpsComponent,
        UserProfileSettingsComponent,
        UserMerchantProfileComponent,
        UserPersonalProfileComponent,
        UserProfileFilesComponent,
        UserAgentProfileComponent,
        AdminEnterpriseWalletBalancePageComponent,
    ],
    imports: [
        BrowserModule,
        FormsModule,
        ReactiveFormsModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        HttpClientModule,
        MaterialModule,
        CKEditorModule,
        ScrollingModule,
        NgSelectModule,
        SyncfusionSharedModule,
        InlineStylesCSPModule,
        ToastrModule.forRoot({
            timeOut: 6000,
            // disableTimeOut: true,
            preventDuplicates: true,
            enableHtml: true,
            positionClass: 'toast-top-right',
        }),
        LoggerModule.forRoot({
            serverLoggingUrl: '/api/logs',
            level: NgxLoggerLevel.DEBUG,
            serverLogLevel: NgxLoggerLevel.ERROR,
        }),
        SharedModule,
        TableComponentModule,
    ],
    providers: [
        { provide: HTTP_INTERCEPTORS, useClass: HttpRequestInterceptor, multi: true },
        DatePipe,
        provideNativeDateAdapter(),
        { provide: LOCALE_ID, useValue: 'en-ZA' }
    ],
    bootstrap: [AppComponent],
})
export class AppModule { }
